import React, { useEffect, useMemo, useState } from "react";
import { Bot, Plus, Pencil, Ban, Trash2, X } from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";

const KNOWN_EVENT_TYPES = [
  { value: "contact.created", label: "contact.created" },
  { value: "contact.updated", label: "contact.updated" },
  { value: "contact.bulk_import.completed", label: "contact.bulk_import.completed" },
  { value: "tag.assigned", label: "tag.assigned" },
  { value: "tag.removed", label: "tag.removed" },
  { value: "note.created", label: "note.created" },
  { value: "note.updated", label: "note.updated" },
  { value: "reminder.created", label: "reminder.created" },
  { value: "reminder.updated", label: "reminder.updated" },
  { value: "reminder.completed", label: "reminder.completed" },
  { value: "message.sent", label: "message.sent" },
  { value: "message.delivered", label: "message.delivered" },
  { value: "message.read", label: "message.read" },
  { value: "cta.clicked", label: "cta.clicked" },
  { value: "campaign.sent", label: "campaign.sent" },
  { value: "lead.status.changed", label: "lead.status.changed" },
  { value: "reminder.triggered", label: "reminder.triggered" },
  { value: "reminder.failed", label: "reminder.failed" },
];

const ACTION_TYPES = [
  { value: "add_tag", label: "add_tag" },
  { value: "create_reminder", label: "create_reminder" },
  { value: "send_template", label: "send_template" },
];

function createAction(type = "add_tag") {
  const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  if (type === "create_reminder") {
    return {
      id,
      type,
      title: "",
      description: "",
      dueInMinutes: 60,
    };
  }
  if (type === "send_template") {
    return {
      id,
      type,
      templateName: "",
      templateParametersText: "",
    };
  }
  return {
    id,
    type: "add_tag",
    tagName: "",
  };
}

function parseActionsForEditor(actionsJson) {
  let parsed = [];
  try {
    parsed = Array.isArray(actionsJson)
      ? actionsJson
      : JSON.parse(actionsJson || "[]");
  } catch {
    parsed = [];
  }

  const actions = [];
  const unsupportedActions = [];

  for (const raw of parsed) {
    const type = String(raw?.type || "").trim().toLowerCase();
    if (type === "add_tag") {
      actions.push({
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        type,
        tagName: raw?.tagName || "",
      });
      continue;
    }
    if (type === "create_reminder") {
      actions.push({
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        type,
        title: raw?.title || "",
        description: raw?.description || "",
        dueInMinutes:
          Number.isFinite(Number(raw?.dueInMinutes)) && Number(raw?.dueInMinutes) > 0
            ? Number(raw?.dueInMinutes)
            : 60,
      });
      continue;
    }
    if (type === "send_template") {
      actions.push({
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        type,
        templateName: raw?.templateName || "",
        templateParametersText: Array.isArray(raw?.templateParameters)
          ? raw.templateParameters.join(", ")
          : "",
      });
      continue;
    }

    unsupportedActions.push(raw);
  }

  return { actions, unsupportedActions, totalCount: parsed.length };
}

function extractApiMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.Message ||
    error?.response?.data?.errorMessage ||
    error?.response?.data?.ErrorMessage ||
    error?.message ||
    fallback
  );
}

function normalizeRule(row) {
  const id = row?.id ?? row?.Id;
  const triggerEventType = row?.triggerEventType ?? row?.TriggerEventType ?? "";
  const actionsJson = row?.actionsJson ?? row?.ActionsJson ?? "[]";
  const isActive = row?.isActive ?? row?.IsActive ?? false;
  const createdAtUtc = row?.createdAtUtc ?? row?.CreatedAtUtc ?? null;
  const parsed = parseActionsForEditor(actionsJson);

  return {
    id,
    triggerEventType,
    actionsJson,
    isActive: !!isActive,
    createdAtUtc,
    actionsCount: parsed.totalCount,
  };
}

function normalizeHealthRow(row) {
  return {
    automationRuleId: row?.automationRuleId ?? row?.AutomationRuleId ?? null,
    lastTriggeredAtUtc: row?.lastTriggeredAtUtc ?? row?.LastTriggeredAtUtc ?? null,
    runsToday: Number(row?.runsToday ?? row?.RunsToday ?? 0) || 0,
    failuresToday: Number(row?.failuresToday ?? row?.FailuresToday ?? 0) || 0,
  };
}

function formatUtcDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

export default function AutomationRules() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState([]);
  const [healthByRuleId, setHealthByRuleId] = useState({});
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presets, setPresets] = useState([]);
  const [creatingFromPresetKey, setCreatingFromPresetKey] = useState("");

  const [showEditor, setShowEditor] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [form, setForm] = useState({
    triggerEventType: "",
    isActive: true,
    actions: [createAction("add_tag")],
    unsupportedActions: [],
  });

  const sortedRules = useMemo(() => {
    return [...rules].sort((a, b) => {
      const aTime = new Date(a.createdAtUtc || 0).getTime();
      const bTime = new Date(b.createdAtUtc || 0).getTime();
      return bTime - aTime;
    });
  }, [rules]);

  const loadRules = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get("/automation-rules", {
        __silentGenericToast: true,
      });
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : [];
      setRules(list.map(normalizeRule).filter(r => r.id));
      await loadHealth();
    } catch (error) {
      toast.error(extractApiMessage(error, "Failed to load automation rules."));
      setRules([]);
      setHealthByRuleId({});
    } finally {
      setLoading(false);
    }
  };

  const loadHealth = async () => {
    try {
      const res = await axiosClient.get("/automation-rules/health", {
        __silentGenericToast: true,
      });
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : [];

      const next = {};
      for (const row of list.map(normalizeHealthRow)) {
        if (!row.automationRuleId) continue;
        next[row.automationRuleId] = row;
      }

      setHealthByRuleId(next);
    } catch {
      setHealthByRuleId({});
    }
  };

  const loadPresets = async () => {
    setPresetsLoading(true);
    try {
      const res = await axiosClient.get("/automation-rules/presets", {
        __silentGenericToast: true,
      });
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : [];

      const normalized = list
        .map(row => ({
          key: row?.key ?? row?.Key,
          title: row?.title ?? row?.Title ?? "",
          description: row?.description ?? row?.Description ?? "",
          triggerEventType: row?.triggerEventType ?? row?.TriggerEventType ?? "",
          actionsCount: Array.isArray(row?.actions ?? row?.Actions)
            ? (row?.actions ?? row?.Actions).length
            : 0,
        }))
        .filter(p => p.key);

      setPresets(normalized);
    } catch (error) {
      toast.error(extractApiMessage(error, "Failed to load presets."));
      setPresets([]);
    } finally {
      setPresetsLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
    loadPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditingRuleId(null);
    setForm({
      triggerEventType: "",
      isActive: true,
      actions: [createAction("add_tag")],
      unsupportedActions: [],
    });
    setShowEditor(true);
  };

  const openEdit = rule => {
    const parsed = parseActionsForEditor(rule.actionsJson);
    if (parsed.unsupportedActions.length > 0) {
      toast.warn(
        "This rule has unsupported action types. They will be preserved, but cannot be edited in this form."
      );
    }

    setEditingRuleId(rule.id);
    setForm({
      triggerEventType: rule.triggerEventType,
      isActive: rule.isActive,
      actions: parsed.actions.length > 0 ? parsed.actions : [createAction("add_tag")],
      unsupportedActions: parsed.unsupportedActions,
    });
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingRuleId(null);
  };

  const setActionType = (index, type) => {
    setForm(prev => {
      const nextActions = [...prev.actions];
      nextActions[index] = createAction(type);
      return { ...prev, actions: nextActions };
    });
  };

  const setActionField = (index, field, value) => {
    setForm(prev => {
      const nextActions = [...prev.actions];
      nextActions[index] = { ...nextActions[index], [field]: value };
      return { ...prev, actions: nextActions };
    });
  };

  const addAction = () => {
    setForm(prev => ({ ...prev, actions: [...prev.actions, createAction("add_tag")] }));
  };

  const removeAction = index => {
    setForm(prev => {
      const nextActions = prev.actions.filter((_, i) => i !== index);
      return {
        ...prev,
        actions: nextActions.length > 0 ? nextActions : [createAction("add_tag")],
      };
    });
  };

  const buildActionsPayload = () => {
    const payload = [];

    for (let i = 0; i < form.actions.length; i += 1) {
      const action = form.actions[i];
      const actionIndex = i + 1;

      if (action.type === "add_tag") {
        const tagName = String(action.tagName || "").trim();
        if (!tagName) {
          toast.warn(`Action ${actionIndex}: Tag name is required.`);
          return null;
        }
        payload.push({ type: "add_tag", tagName });
        continue;
      }

      if (action.type === "create_reminder") {
        const dueInMinutes = Number(action.dueInMinutes || 0);
        if (!Number.isFinite(dueInMinutes) || dueInMinutes <= 0) {
          toast.warn(`Action ${actionIndex}: Due in minutes must be greater than 0.`);
          return null;
        }
        payload.push({
          type: "create_reminder",
          title: String(action.title || "").trim() || "Automation follow-up",
          description: String(action.description || "").trim() || undefined,
          dueInMinutes: Math.floor(dueInMinutes),
        });
        continue;
      }

      if (action.type === "send_template") {
        const templateName = String(action.templateName || "").trim();
        if (!templateName) {
          toast.warn(`Action ${actionIndex}: Template name is required.`);
          return null;
        }
        const templateParameters = String(action.templateParametersText || "")
          .split(",")
          .map(x => x.trim())
          .filter(Boolean);

        payload.push({
          type: "send_template",
          templateName,
          templateParameters,
        });
        continue;
      }
    }

    if (Array.isArray(form.unsupportedActions) && form.unsupportedActions.length > 0) {
      return [...payload, ...form.unsupportedActions];
    }
    return payload;
  };

  const saveRule = async () => {
    const triggerEventType = String(form.triggerEventType || "").trim();
    if (!triggerEventType) {
      toast.warn("Trigger event type is required.");
      return;
    }

    const actions = buildActionsPayload();
    if (!actions) return;
    if (actions.length === 0) {
      toast.warn("At least one action is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        triggerEventType,
        actionsJson: JSON.stringify(actions),
        isActive: !!form.isActive,
      };

      if (editingRuleId) {
        await axiosClient.put(`/automation-rules/${editingRuleId}`, payload, {
          __silentGenericToast: true,
        });
        toast.success("Automation rule updated.");
      } else {
        await axiosClient.post("/automation-rules", payload, {
          __silentGenericToast: true,
        });
        toast.success("Automation rule created.");
      }

      closeEditor();
      await loadRules();
    } catch (error) {
      toast.error(extractApiMessage(error, "Failed to save automation rule."));
    } finally {
      setSaving(false);
    }
  };

  const disableRule = async rule => {
    if (!window.confirm(`Disable rule for "${rule.triggerEventType}"?`)) return;

    try {
      await axiosClient.delete(`/automation-rules/${rule.id}`, {
        __silentGenericToast: true,
      });
      toast.success("Automation rule disabled.");
      await loadRules();
    } catch (error) {
      toast.error(extractApiMessage(error, "Failed to disable rule."));
    }
  };

  const createFromPreset = async preset => {
    if (!preset?.key) return;

    setCreatingFromPresetKey(preset.key);
    try {
      await axiosClient.post(
        `/automation-rules/from-preset/${encodeURIComponent(preset.key)}`,
        null,
        { __silentGenericToast: true }
      );
      toast.success(`Preset "${preset.title || preset.key}" applied.`);
      await loadRules();
    } catch (error) {
      toast.error(extractApiMessage(error, "Failed to create rule from preset."));
    } finally {
      setCreatingFromPresetKey("");
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#f5f6f7]">
      <div className="max-w-6xl mx-auto px-6 pt-10 pb-8">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <Bot size={17} />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Automation Rules</h1>
              <p className="text-xs text-slate-600 mt-0.5">
                Create event-driven rules to add tags, create reminders, and send templates.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-xs font-semibold bg-emerald-700 text-white border border-emerald-700 hover:bg-emerald-800 whitespace-nowrap"
          >
            <Plus size={15} />
            Create rule
          </button>
        </div>

        <div className="bg-white rounded-md border border-slate-100 shadow-sm px-6 py-5">
          <div className="mb-5">
            <h2 className="text-sm font-semibold text-slate-800 mb-2">Presets</h2>
            {presetsLoading ? (
              <div className="text-sm text-slate-500">Loading presets...</div>
            ) : presets.length === 0 ? (
              <div className="text-sm text-slate-500">No presets available.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {presets.map(preset => (
                  <div
                    key={preset.key}
                    className="rounded-md border border-slate-200 px-4 py-3 bg-slate-50/40"
                  >
                    <div className="text-sm font-semibold text-slate-800">{preset.title}</div>
                    <div className="text-xs text-slate-600 mt-1">{preset.description}</div>
                    <div className="text-[11px] text-slate-500 mt-2">
                      Trigger: {preset.triggerEventType} | Actions: {preset.actionsCount}
                    </div>
                    <button
                      type="button"
                      onClick={() => createFromPreset(preset)}
                      disabled={creatingFromPresetKey === preset.key}
                      className="mt-3 inline-flex items-center gap-1 rounded-md border border-emerald-200 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                    >
                      <Plus size={13} />
                      {creatingFromPresetKey === preset.key ? "Creating..." : "Use preset"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 pt-5">
          {loading ? (
            <div className="py-10 text-sm text-slate-500">Loading automation rules...</div>
          ) : sortedRules.length === 0 ? (
            <div className="py-10 text-sm text-slate-500">
              No rules yet. Create your first automation rule.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100">
                    <th className="py-2 pr-4">Trigger Event</th>
                    <th className="py-2 pr-4">Actions</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Created</th>
                    <th className="py-2 pr-4">Last Triggered</th>
                    <th className="py-2 pr-4">Runs Today</th>
                    <th className="py-2 pr-4">Failures Today</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRules.map(rule => {
                    const health = healthByRuleId[rule.id] || {
                      lastTriggeredAtUtc: null,
                      runsToday: 0,
                      failuresToday: 0,
                    };

                    return (
                      <tr key={rule.id} className="border-b border-slate-100 last:border-b-0">
                        <td className="py-3 pr-4 text-slate-800 font-medium">{rule.triggerEventType}</td>
                        <td className="py-3 pr-4 text-slate-600">{rule.actionsCount}</td>
                        <td className="py-3 pr-4">
                          <span
                            className={`inline-flex px-2 py-1 rounded-md text-[11px] font-semibold ${
                              rule.isActive
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {rule.isActive ? "Active" : "Disabled"}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-slate-500">{formatUtcDate(rule.createdAtUtc)}</td>
                        <td className="py-3 pr-4 text-slate-500">{formatUtcDate(health.lastTriggeredAtUtc)}</td>
                        <td className="py-3 pr-4 text-slate-600">{health.runsToday}</td>
                        <td className="py-3 pr-4 text-slate-600">{health.failuresToday}</td>
                        <td className="py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openEdit(rule)}
                              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                            >
                              <Pencil size={13} />
                              Edit
                            </button>
                            {rule.isActive ? (
                              <button
                                type="button"
                                onClick={() => disableRule(rule)}
                                className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
                              >
                                <Ban size={13} />
                                Disable
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          </div>
        </div>
      </div>

      {showEditor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-md shadow-xl w-full max-w-3xl relative">
            <button
              type="button"
              onClick={closeEditor}
              className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <div className="px-6 py-5 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">
                {editingRuleId ? "Edit rule" : "Create rule"}
              </h2>
            </div>

            <div className="p-6 space-y-5 max-h-[75vh] overflow-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-600">Trigger Event Type</label>
                  <select
                    value={form.triggerEventType}
                    onChange={e =>
                      setForm(prev => ({ ...prev, triggerEventType: e.target.value }))
                    }
                    className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="">Select event type</option>
                    {KNOWN_EVENT_TYPES.map(eventType => (
                      <option key={eventType.value} value={eventType.value}>
                        {eventType.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={e =>
                        setForm(prev => ({ ...prev, isActive: e.target.checked }))
                      }
                      className="accent-emerald-600"
                    />
                    Rule active
                  </label>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800">Actions</h3>
                  <button
                    type="button"
                    onClick={addAction}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <Plus size={13} />
                    Add action
                  </button>
                </div>

                {form.actions.map((action, index) => (
                  <div key={action.id} className="rounded-md border border-slate-200 p-4 bg-slate-50/40">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs font-semibold text-slate-500">Action {index + 1}</div>
                      <button
                        type="button"
                        onClick={() => removeAction(index)}
                        className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700"
                      >
                        <Trash2 size={13} />
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-slate-600">Type</label>
                        <select
                          value={action.type}
                          onChange={e => setActionType(index, e.target.value)}
                          className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                        >
                          {ACTION_TYPES.map(actionType => (
                            <option key={actionType.value} value={actionType.value}>
                              {actionType.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {action.type === "add_tag" ? (
                        <div>
                          <label className="text-xs font-medium text-slate-600">Tag name</label>
                          <input
                            type="text"
                            value={action.tagName || ""}
                            onChange={e => setActionField(index, "tagName", e.target.value)}
                            className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                            placeholder="e.g. hot-lead"
                          />
                        </div>
                      ) : null}

                      {action.type === "create_reminder" ? (
                        <>
                          <div>
                            <label className="text-xs font-medium text-slate-600">Title</label>
                            <input
                              type="text"
                              value={action.title || ""}
                              onChange={e => setActionField(index, "title", e.target.value)}
                              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                              placeholder="Reminder title"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-600">Due in minutes</label>
                            <input
                              type="number"
                              min={1}
                              value={action.dueInMinutes ?? 60}
                              onChange={e => setActionField(index, "dueInMinutes", e.target.value)}
                              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="text-xs font-medium text-slate-600">Description</label>
                            <input
                              type="text"
                              value={action.description || ""}
                              onChange={e => setActionField(index, "description", e.target.value)}
                              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                              placeholder="Optional description"
                            />
                          </div>
                        </>
                      ) : null}

                      {action.type === "send_template" ? (
                        <>
                          <div>
                            <label className="text-xs font-medium text-slate-600">Template name</label>
                            <input
                              type="text"
                              value={action.templateName || ""}
                              onChange={e => setActionField(index, "templateName", e.target.value)}
                              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                              placeholder="WhatsApp template name"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-600">
                              Template parameters (comma separated)
                            </label>
                            <input
                              type="text"
                              value={action.templateParametersText || ""}
                              onChange={e =>
                                setActionField(index, "templateParametersText", e.target.value)
                              }
                              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                              placeholder="John, 1250, 12 Feb"
                            />
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeEditor}
                className="px-4 py-2 rounded-md border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveRule}
                disabled={saving}
                className="px-4 py-2 rounded-md bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 disabled:opacity-60"
              >
                {saving ? "Saving..." : editingRuleId ? "Update rule" : "Create rule"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
